import { createSelector } from 'reselect';
import _ from 'lodash';

import CONST from '@shared/task/constants';
import { getGroupsWithSubGroups } from '@common/utils/company';
import { searchObjects } from '@common/utils/search';
import { USER_ROLES } from '@common/utils/roles';
import * as peopleHelper from '@common/utils/people_helpers';

const personNameSortFunction = person =>
  _.get(person, 'user.full_name') || _.get(person, 'company.name') || _.get(person, 'full_name');
const getCurrentUser = state => state.app.user;
const personHasMessagingAccess = state =>
  _.get(getCurrentUser(state), 'company_person.messaging_access', false);
export const getCompany = state => _.get(state, 'datastore.company.company');
export const getCompanyImage = (state, imageType) => _.get(getCompany(state), imageType);

// permissions
export const getPermissions = state => _.get(state, 'datastore.company.permissions');
export const getDepartmentsPermissions = state => _.get(getPermissions(state), 'department');
export const getDepartment = (state, department) =>
  _.get(getDepartmentsPermissions(state), department);

// Settings
export const getSettings = state => _.get(state, 'datastore.company.settings');
export const getSetting = (state, setting) => _.get(getSettings(state), setting);
export const getSuppliesEnabled = state => getSetting(state, 'supplies_feature') === 'enabled';
export const getCurrency = state =>
  _.get(getSettings(state), 'currency') || CONST.DEFAULT_CURRENCY.code;
const getCompanyOrganization = state => _.get(getSettings(state), 'organization');
export const isGroupOrganization = createSelector(
  getCompanyOrganization,
  organization => _.isEqual(organization, 'group'),
);
export const getBusinessHours = state => _.get(state, 'datastore.company.businessHours', {});

// Supplies
export const getSupplies = state => state.datastore.company.supplies;
export const getSupply = (state, supplyId) => _.get(getSupplies(state), supplyId, {});

// Readiness
export const getReadinessWorkflows = state =>
  _.get(getSettings(state), 'property_readiness_workflows');
export const propertyReadinessWorkflowsIsEnabled = state =>
  _.size(getReadinessWorkflows(state)) > 0;
export const getPropertyReadiness = state => _.get(getSettings(state), 'property_readiness');
export const isPropertyReadinessEnabled = state => getPropertyReadiness(state) === 'enabled';
export const isPropertyReadinessOld = state => getPropertyReadiness(state) === 'old';

export const getAcceptDeclineEnabled = state =>
  _.get(getSettings(state), 'user_accept_decline_tasks') === 'enabled';
export const getTaskSeriesEnabled = state =>
  _.get(getSettings(state), 'task_series_feature') === 'enabled';
export const getApproveTasksEnabled = state =>
  _.get(getSettings(state), 'approve_tasks') === 'enabled';
export const getUserPlanningEnabled = state =>
  _.get(getSettings(state), 'schedule_user_planning_feature') === 'enabled';
export const getExternalPeopleEnabled = state =>
  _.get(getSettings(state), 'task_external_people') === 'enabled';

// Reservations
export const reservationUploadIsEnabled = state =>
  _.get(getSettings(state), 'reservation_uploads') === 'enabled';

// Company Attributes
export const getAllHomeTags = state => state.datastore.company.homeTags;
export const getBuildings = state => state.datastore.company.buildings;
export const getCompanyGroups = state => state.datastore.company.companyGroups;
export const getHomes = state => state.datastore.company.homes;
export const getTags = state => state.datastore.company.tags;
export const getTemplates = state => state.datastore.company.templates;
export const getTemplate = (state, templateId) => _.get(getTemplates(state), templateId);
export const getUserPropertyIds = state => state.datastore.company.userPropertyIds;
export const getWorkers = state => state.datastore.company.workers;
export const getWorkflows = state => state.datastore.company.workflows;
export const getCompanyId = state => _.get(getCompany(state), 'id');
export const getCompanyName = state => _.get(getCompany(state), 'name');
const getFeatures = state => state.datastore.company.features;

export const getBuilding = (state, buildingId) => _.get(getBuildings(state), buildingId, null);
export const getHome = (state, homeId) => _.get(getHomes(state), homeId, null);
export const getHomeTags = (state, homeId) => _.get(getAllHomeTags(state), homeId, null);
export const getHomeWorkers = (state, homeId) => _.get(getWorkers(state), homeId, null);
export const getHomeWorkersByDepartment = (state, homeId) => {
  const homeDepartmentWorkers = _.filter(getHomeWorkers(state, homeId), worker =>
    _.has(worker, 'type_department'),
  );
  return _.groupBy(homeDepartmentWorkers, 'type_department.code');
};
export const getHomeWorkersByTemplate = (state, homeId) => {
  const homeTemplateWorkers = _.filter(getHomeWorkers(state, homeId), worker =>
    _.has(worker, 'template_id'),
  );
  return _.groupBy(homeTemplateWorkers, 'template_id');
};
export const getHomeDepartmentWorkers = (state, homeId, department) =>
  _.get(getHomeWorkersByDepartment(state, homeId), department, []);

export const getHomeTemplateEstimates = (state, homeId) =>
  _.get(getHome(state, homeId), 'template_estimates');

export const getWorkflow = (state, workflowId) => _.get(getWorkflows(state), workflowId, null);

// People
export const getPeople = state => _.get(state, 'datastore.company.people');
export const getPerson = (state, personId) => _.get(getPeople(state), personId);
export const getAssignablePeopleIds = (state, peopleIds) =>
  _.filter(peopleIds, personId => {
    const personObject = getPerson(state, personId);
    return _.get(personObject, 'is_assignable');
  });

export const getActivePeople = createSelector(
  getPeople,
  people => _.filter(people, person => _.get(person, 'active')),
);

export const getUserProperties = createSelector(
  getUserPropertyIds,
  getHomes,
  (propertyIds, properties) => _.map(propertyIds, propertyId => _.get(properties, propertyId)),
);

export const getPersonAvailability = (state, personId) =>
  _.get(getPerson(state, personId), 'availability');

export const getPersonAvailabilityByDOW = (state, personId, dayOfWeek) =>
  _.get(getPersonAvailability(state, personId), dayOfWeek);

const getAvailabilityFromSixteenHours = avail => {
  if (_.isEmpty(avail)) {
    return null;
  }

  const results = [];
  const defaultStartHour = '06:00:00';
  const defaultEndHour = '22:00:00';
  _.map(avail, interval => {
    if (interval.end >= defaultEndHour && interval.start > defaultStartHour) {
      results.push({ end: defaultEndHour, start: interval.start });
    } else if (interval.end < defaultEndHour && interval.start <= defaultStartHour) {
      results.push({ end: interval.end, start: defaultStartHour });
    } else if (interval.end <= defaultEndHour && interval.start >= defaultStartHour) {
      results.push({ end: interval.end, start: interval.start });
    } else if (interval.end > defaultEndHour && interval.start < defaultStartHour) {
      results.push({ end: defaultEndHour, start: defaultStartHour });
    }
  });
  return results;
};

const getPersonAvailabilityFromSixteenHours = person => {
  const personResults = {};
  const personAvailability = _.get(person, 'availability');
  _.forEach(personAvailability, (avail, dow) => {
    const availFromSixteenHours = getAvailabilityFromSixteenHours(avail);
    if (!_.isNull(availFromSixteenHours)) {
      personResults[dow] = availFromSixteenHours;
    }
  });
  return personResults;
};

export const getAllPeopleAvailabilityFromSixteenHours = createSelector(
  getPeople,
  people => {
    const results = {};
    _.forEach(people, person => {
      results[person.id] = getPersonAvailabilityFromSixteenHours(person);
    });
    return results;
  },
);

export const getPersonAvailabilityByDOWFromSixteenHours = (state, personId, dayOfWeek) => {
  const personAvailByDOW = getPersonAvailabilityByDOW(state, personId, dayOfWeek);
  return getAvailabilityFromSixteenHours(personAvailByDOW) || [];
};

// Workflows
export const getSortedWorkflows = state => _.sortBy(getWorkflows(state), wf => _.toLower(wf.name));
export const getValidWorkflows = state =>
  _.sortBy(
    _.filter(getWorkflows(state), workflow => _.size(workflow.setting_task_templates) > 0),
    wf => _.toLower(wf.name),
  );

// Groups
export const getGroupsByParent = createSelector(
  getCompanyGroups,
  companyGroups => _.groupBy(companyGroups, 'parent_group_id'),
);

export const countPropertiesOnEachGroup = state => _.countBy(getHomes(state), 'company_group_id');

export const getFilteredGroups = (state, filterGroups) =>
  getGroupsWithSubGroups(getCompanyGroups(state), filterGroups);

// Users for Properties
export const getGroupToPersonMap = createSelector(
  getActivePeople,
  getGroupsByParent,
  (people, groupsByParent) => {
    const allGroupPeople = [];
    const groupToPersonMap = {};
    _.forEach(people, person => {
      const personId = _.get(person, 'id');
      if (_.get(person, 'group_all_access')) {
        allGroupPeople.push(personId);
      } else {
        _.forEach(person.groups, personGroup => {
          const groupId = _.get(personGroup, 'id');
          if (!_.has(groupToPersonMap, groupId)) {
            groupToPersonMap[groupId] = [];
          }
          groupToPersonMap[groupId].push(personId);

          const subGroups = groupsByParent[groupId];
          if (subGroups && subGroups.length > 0) {
            _.forEach(subGroups, subGroup => {
              const subGroupId = _.get(subGroup, 'id');
              if (!_.has(groupToPersonMap, subGroupId)) {
                groupToPersonMap[subGroupId] = [];
              }
              groupToPersonMap[subGroupId].push(personId);
            });
          }
        });
      }
    });

    _.forEach(_.keys(groupToPersonMap), groupId => {
      groupToPersonMap[groupId] = [...groupToPersonMap[groupId], ...allGroupPeople];
    });
    groupToPersonMap.all = allGroupPeople;
    return groupToPersonMap;
  },
);

const getPeopleForGroups = (state, companyGroupId) => {
  const groupToPersonMap = getGroupToPersonMap(state);
  if (_.has(groupToPersonMap, companyGroupId)) {
    return groupToPersonMap[companyGroupId];
  }
  return groupToPersonMap.all;
};

export const getAllowedPersonIdsForHome = (state, homeId) => {
  if (isGroupOrganization(state)) {
    const home = getHome(state, homeId);
    return getPeopleForGroups(state, _.get(home, 'company_group.id'));
  }
  return _.map(getPeople(state), person => person.id);
};

/**
 * @param {Array} state
 * @param {Array} people
 * @param {Boolean} homeId
 * @param {Boolean} externalPeopleEnabled - if true - service partners are not filtered by groups
 * @return {Array}
 */
const getPeopleForSorting = (state, people, homeId, externalPeopleEnabled) => {
  if (homeId) {
    const homePeopleIds = getAllowedPersonIdsForHome(state, homeId);
    return _.filter(
      people,
      person =>
        _.indexOf(homePeopleIds, person.id) > -1 ||
        (externalPeopleEnabled && peopleHelper.isServicePartner(person)),
    );
  }

  return people;
};

/**
 * @param {Array} state
 * @param {Boolean} homeId
 * @param {Boolean} department
 * @param {Boolean} externalPeopleEnabled - if true - service partners are not filtered by groups
 * @return {Array}
 */
export const getSortedPeopleByPropertyList = (state, homeId, department, externalPeopleEnabled) => {
  const propertyWorkers = getHomeWorkers(state, homeId);
  const availablePeople = getPeopleForSorting(
    state,
    getActivePeople(state),
    homeId,
    externalPeopleEnabled,
  );
  const workerIds =
    propertyWorkers && department
      ? _.map(
          _.filter(propertyWorkers, worker => department === _.get(worker, 'type_department.code')),
          worker => worker.company_people_id,
        )
      : [];
  return _.orderBy(
    _.map(availablePeople, person => ({
      ...person,
      default: _.indexOf(workerIds, person.id) > -1,
    })),
    ['default', personNameSortFunction],
    ['desc', 'asc'],
  );
};

export const getFilteredPropertyIdsV2 = (homes, homeTags, companyGroups, filters, searchQuery) => {
  let properties = _.values(homes);
  if (_.size(filters.buildings) > 0) {
    properties = _.filter(
      properties,
      property => _.indexOf(filters.buildings, property.building_id) >= 0,
    );
  }

  if (_.size(filters.groups) > 0) {
    const filteredGroups = getGroupsWithSubGroups(companyGroups, filters.groups);
    properties = _.filter(
      properties,
      property => _.indexOf(filteredGroups, property.company_group_id) >= 0,
    );
  }

  if (_.size(filters.tags) > 0) {
    properties = _.filter(
      properties,
      property => _.size(_.intersection(filters.tags, homeTags[property.id])) > 0,
    );
  }

  if (_.size(searchQuery) > 0) {
    properties = searchObjects(properties, searchQuery, [
      'name',
      'address1',
      'address2',
      'city',
      'zipcode',
      'marketing_code',
      'internal_code',
    ]);
  }

  return _.map(properties, property => property.id);
};

// Filtering
export const getFilteredPropertyIds = (state, filters, searchQuery) => {
  let properties = _.values(getHomes(state));
  const homeTags = getAllHomeTags(state);

  if (_.size(filters.buildings) > 0) {
    properties = _.filter(
      properties,
      property => _.indexOf(filters.buildings, property.building_id) >= 0,
    );
  }

  if (_.size(filters.groups) > 0) {
    const filteredGroups = getFilteredGroups(state, filters.groups);
    properties = _.filter(
      properties,
      property => _.indexOf(filteredGroups, property.company_group_id) >= 0,
    );
  }

  if (_.size(filters.tags) > 0) {
    properties = _.filter(
      properties,
      property => _.size(_.intersection(filters.tags, homeTags[property.id])) > 0,
    );
  }

  if (_.size(searchQuery) > 0) {
    properties = searchObjects(properties, searchQuery, [
      'name',
      'address1',
      'address2',
      'city',
      'zipcode',
      'marketing_code',
      'internal_code',
    ]);
  }

  return _.map(properties, property => property.id);
};

// Service Partners
export const isServicePartner = state => _.get(getCompany(state), 'is_service_partner');

export const getPropertyFeature = createSelector(
  getFeatures,
  features => _.includes(features, 'properties'),
);

export const getTaskFeature = createSelector(
  getFeatures,
  features => _.includes(features, 'tasks'),
);

export const getMessageFeature = createSelector(
  getFeatures,
  features => _.includes(features, 'messaging'),
);

export const isMessageFeatureEnabled = createSelector(
  getMessageFeature,
  personHasMessagingAccess,
  (features, haveAccess) => features && haveAccess,
);

export const isMessagingAvailableForPerson = (state, personId) => {
  const features = getMessageFeature(state);
  const role = _.get(getPerson(state, personId), 'type_role.code', '');

  return (
    features &&
    (role === USER_ROLES.ADMINISTRATOR ||
      role === USER_ROLES.SUPERVISOR ||
      role === USER_ROLES.OFFICE)
  );
};

export const getNavigationDisplaySetting = createSelector(
  getSettings,
  settings => _.get(settings, 'task_navigation_display'),
);

export const isMapFeatureEnabled = state =>
  _.isEqual(getSetting(state, 'dashboard_map'), 'enabled');
